import { useState } from 'react';
import type { ProgramPublicProfile } from '@/mocks/programProfile';
import type { RadioEvent } from '@/mocks/adminEvents';
import { eventTypeIcons, eventStatusColors } from '@/mocks/adminEvents';

interface Props {
  profile: ProgramPublicProfile;
}

export default function ProgramEventsSection({ profile }: Props) {
  const [activeTab, setActiveTab] = useState<'proximos' | 'finalizados'>('proximos');
  const [interestedEvents, setInterestedEvents] = useState<Set<number>>(new Set());

  const toggleInterested = (id: number) => {
    setInterestedEvents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filtered = profile.events.filter((e) => {
    if (activeTab === 'proximos') return e.status === 'publicado';
    return e.status === 'finalizado' || e.status === 'cancelado';
  });

  return (
    <section className="bg-background-50 rounded-2xl border border-background-200/70 p-5 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
            <i className="ri-calendar-event-line text-accent-600 text-sm"></i>
          </div>
          <h2 className="text-base font-bold text-foreground-950">Eventos</h2>
          <span className="text-xs text-foreground-400">{profile.events.length} eventos</span>
        </div>

        {/* Tabs */}
        <div className="flex items-center bg-background-100 rounded-full p-0.5 self-start">
          {(['proximos', 'finalizados'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === tab
                  ? 'bg-background-50 text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              {tab === 'proximos' ? 'Próximos' : 'Finalizados'}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-14 h-14 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-calendar-event-line text-2xl text-foreground-300"></i>
          </div>
          <p className="text-sm font-semibold text-foreground-500">Sin eventos {activeTab === 'proximos' ? 'próximos' : 'finalizados'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {filtered.map((event) => (
            <EventCard
              key={event.id}
              event={event}
              isInterested={interestedEvents.has(event.id)}
              onToggleInterested={() => toggleInterested(event.id)}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function EventCard({ event, isInterested, onToggleInterested }: { event: RadioEvent; isInterested: boolean; onToggleInterested: () => void }) {
  const statusStyle = eventStatusColors[event.status];
  const isPast = event.status === 'finalizado' || event.status === 'cancelado';

  return (
    <div className={`rounded-xl border overflow-hidden transition-colors ${
      event.highlighted ? 'border-accent-300 bg-accent-50/30' : 'border-background-200/70 bg-background-100/70'
    } ${!isPast ? 'hover:border-accent-300/60' : 'opacity-80'}`}>
      {/* Image */}
      <div className="w-full h-40 overflow-hidden relative">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" />
        <span className={`absolute top-3 left-3 px-2 py-0.5 rounded-md text-[10px] font-semibold ${statusStyle.bg} ${statusStyle.text}`}>
          {event.status === 'publicado' ? 'Próximo' : event.status === 'finalizado' ? 'Finalizado' : 'Cancelado'}
        </span>
        {event.highlighted && !isPast && (
          <span className="absolute top-3 right-3 px-2 py-0.5 rounded-md bg-accent-500 text-white text-[10px] font-bold">
            Destacado
          </span>
        )}
      </div>

      <div className="p-4">
        {/* Type */}
        <div className="flex items-center gap-1.5 mb-2">
          <i className={`${eventTypeIcons[event.type] || 'ri-calendar-line'} text-xs text-foreground-400`}></i>
          <span className="text-[10px] text-foreground-500 font-medium uppercase">{event.type.replace('_', ' ')}</span>
        </div>

        <h3 className="text-sm font-bold text-foreground-900 mb-1.5 line-clamp-1">{event.title}</h3>

        <div className="space-y-1 mb-3">
          <div className="flex items-center gap-1.5 text-[10px] text-foreground-500">
            <i className="ri-calendar-line"></i>
            {new Date(event.date).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
            <span className="text-foreground-300">·</span>
            <span>{event.time}</span>
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-foreground-500">
            <i className="ri-map-pin-line"></i>
            <span className="line-clamp-1">{event.location}</span>
          </div>
        </div>

        {/* Stats + Action */}
        <div className="flex items-center justify-between gap-3 pt-3 border-t border-background-200/70">
          <div className="flex items-center gap-3 text-[10px] text-foreground-400">
            <span className="flex items-center gap-1">
              <i className="ri-user-line"></i>
              {event.participants}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-heart-line"></i>
              {isInterested ? event.interested + 1 : event.interested}
            </span>
          </div>
          {!isPast && (
            <button
              onClick={onToggleInterested}
              className={`flex items-center gap-1 px-3 py-1 rounded-full text-[10px] font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                isInterested
                  ? 'bg-accent-100 text-accent-700'
                  : 'bg-secondary-100 text-secondary-600 hover:bg-secondary-200'
              }`}
            >
              <i className={isInterested ? 'ri-check-line' : 'ri-star-line'}></i>
              {isInterested ? 'Interesado' : 'Me interesa'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}